import { useEffect, useState } from 'react';

import { safeToTypeset } from './mathSafety';

/**
 * A formula rendered for reading, not for editing.
 *
 * ## Why this is separate from `MathField`
 *
 * A `<math-field>` is an input: it takes focus, shows a caret, handles keys and costs a real
 * element per instance. The example buttons under the formula field need none of that — they
 * are six pieces of read-only mathematics, and mounting six editors to display them would be
 * both heavy and wrong, since clicking one would put a caret in it rather than choosing it.
 *
 * `convertLatexToMarkup` is MathLive's answer: LaTeX in, HTML out, no element, no state.
 *
 * ## Why the markup is injected rather than parsed
 *
 * `dangerouslySetInnerHTML` earns its name, so the question deserves answering rather than
 * ignoring. The alternative — parsing MathLive's output into React elements — would be
 * re-implementing a renderer to no benefit.
 *
 * This used to be answered by "everything here is a compile-time constant", and it warned that
 * pointing it at something a user typed would mean deciding again. That happened: the problem
 * box previews what a teacher writes, and a teacher's commonest way of writing a problem is to
 * paste it from somewhere else. So the decision was made again and it is recorded in
 * `mathSafety.ts` — MathLive implements `\href`, `\class` and `\htmlData`, a formula carrying
 * one is refused before the renderer is asked, and it falls back to showing its own source.
 *
 * A refusal rather than a filter, because stripping commands out of somebody else's macro
 * language is a promise this file cannot keep across their next release.
 *
 * ## Fonts
 *
 * The static markup gets its glyphs from `mathlive/static.css`, whose `@font-face` rules are
 * resolved by the bundler into hashed assets. That is a *second* copy of the same fonts, since
 * `public/mathlive-fonts` already serves the editor — the two paths cannot share, because the
 * editor needs a stable directory it can be told about at runtime and the bundler needs to
 * hash its own. A few hundred kilobytes of duplication, in an asynchronous chunk, in exchange
 * for both working offline.
 */

/** The module exports this at runtime; its browser type definitions omit it. */
interface StaticRenderer {
  convertLatexToMarkup(latex: string): string;
}

let renderer: Promise<StaticRenderer | null> | null = null;

function loadRenderer(): Promise<StaticRenderer | null> {
  renderer ??= Promise.all([import('mathlive'), import('mathlive/static.css')])
    .then(([module]) => {
      const convert = (module as unknown as Partial<StaticRenderer>).convertLatexToMarkup;
      return typeof convert === 'function' ? { convertLatexToMarkup: convert } : null;
    })
    .catch(() => null);

  return renderer;
}

export interface MathTextProps {
  /**
   * LaTeX.
   *
   * It may now come from a person, which the note above said would require the decision to be
   * made again. It was: see `mathSafety.ts`. A formula carrying one of MathLive's
   * attribute-injecting commands is shown as its own source instead of being typeset, so this
   * component never puts author-controlled attributes into the page.
   */
  readonly latex: string;
}

export function MathText({ latex }: MathTextProps) {
  const [markup, setMarkup] = useState<string | null>(null);

  /*
   * Decided during the render rather than inside the effect, and it matters twice.
   *
   * The effect would have to call `setMarkup(null)` to withdraw markup left over from a
   * previous, safe formula — a synchronous state write in an effect, which cascades renders.
   * Computing it here makes the refusal a property of *this* render, so a formula that turns
   * hostile between two renders can never be drawn from stale state.
   */
  const allowed = safeToTypeset(latex);

  useEffect(() => {
    let cancelled = false;
    // Refused before the renderer is even asked, so there is no path where the markup exists.
    if (!allowed) return;

    void loadRenderer().then((loaded) => {
      if (cancelled || loaded === null) return;

      /*
       * Guarded, because typesetting is a second thing that can fail after loading succeeded.
       *
       * Without this a formula the renderer chokes on produces an *unhandled rejection* — no
       * message, no fallback, and a console warning that points at a promise rather than at
       * the formula. Falling back to the source is the same answer as failing to load at all,
       * which is the answer that leaves the panel usable.
       */
      try {
        setMarkup(loaded.convertLatexToMarkup(latex));
      } catch {
        setMarkup(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [latex, allowed]);

  /*
   * The raw LaTeX until the renderer arrives, and for good if it never does.
   *
   * A blank button would be worse than an ugly one: the user still has to be able to tell the
   * examples apart, and `x^2` read as text is perfectly legible.
   */
  if (!allowed || markup === null) return <span>{latex}</span>;

  return <span dangerouslySetInnerHTML={{ __html: markup }} />;
}
